import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { useEffect, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "../lib/api";
import ChatSidebar from "./ai-coach/ChatSidebar";

export default function Layout() {
  const { user, activeProfileId, switchProfile, signOut, checkSession } =
    useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [resendStatus, setResendStatus] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  // Fetch customers if user is a trainer
  const { data: customersData } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      return client.customers.getCustomers();
    },
    enabled: user?.role === "trainer",
  });

  // Find active customer if switched
  const activeCustomer = customersData?.customers.find(
    (c) => c.id === activeProfileId,
  );
  const displayEmail = activeCustomer?.email || user?.email;

  // Listen for unauthorized events from API interceptor
  useEffect(() => {
    const handleUnauthorized = () => {
      console.log("Received auth:unauthorized event");
      useAuthStore.getState().handleUnauthorized();
      navigate("/login");
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, [navigate]);

  // Listen for toast events dispatched by the API guard
  useEffect(() => {
    const handleToast = (e: Event) => {
      const msg = (e as CustomEvent<{ message: string }>).detail.message;
      setToastMsg(msg);
      setTimeout(() => setToastMsg(null), 4000);
    };
    window.addEventListener("ui:toast", handleToast);
    return () => window.removeEventListener("ui:toast", handleToast);
  }, []);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleResend = async () => {
    if (!user?.email) return;
    setResendStatus("sending");
    try {
      await client.default.sendVerificationEmail({
        requestBody: {
          email: user.email,
          callbackURL: window.location.origin + "/",
        },
      });
      setResendStatus("sent");
      setCooldown(30);
    } catch {
      setResendStatus("error");
    }
  };

  const handleCheckVerified = async () => {
    setCheckingVerification(true);
    try {
      await checkSession();
    } finally {
      setCheckingVerification(false);
    }
  };

  const showEmailBanner = user?.emailVerified === false;
  const showTrainerBanner = !!(
    user?.role === "trainer" &&
    activeProfileId &&
    activeCustomer
  );
  const mainMt =
    showEmailBanner && showTrainerBanner
      ? "mt-44 md:mt-0"
      : showEmailBanner || showTrainerBanner
        ? "mt-28 md:mt-0"
        : "mt-14 md:mt-0";

  return (
    <div className="min-h-screen bg-bg-secondary pb-16 md:pb-0">
      {/* Desktop Navigation */}
      <nav className="bg-bg-primary shadow-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <Link
                to="/"
                className="flex items-center gap-3 px-2 text-xl font-bold"
              >
                <img
                  src="/icons/icon.png"
                  alt="TrainrFlo"
                  className="h-8 w-8"
                />
                <span>
                  <span className="text-indigo-600">train</span>
                  <span className="text-orange-500">r</span>
                  <span className="text-indigo-600">flo</span>
                </span>
              </Link>
              <div className="ml-6 flex space-x-8">
                <Link
                  to="/goals"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                    location.pathname.startsWith("/programs")
                      ? "text-indigo-600 border-b-2 border-indigo-600"
                      : "text-gray-500 hover:text-text-primary"
                  }`}
                >
                  Goals
                </Link>
                <Link
                  to="/programs"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                    location.pathname.startsWith("/programs")
                      ? "text-indigo-600 border-b-2 border-indigo-600"
                      : "text-gray-500 hover:text-text-primary"
                  }`}
                >
                  Programs
                </Link>
                <Link
                  to="/videos"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                    isActive("/videos")
                      ? "text-indigo-600 border-b-2 border-indigo-600"
                      : "text-gray-500 hover:text-text-primary"
                  }`}
                >
                  Exercises
                </Link>
                <Link
                  to="/sessions"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                    location.pathname.startsWith("/sessions")
                      ? "text-indigo-600 border-b-2 border-indigo-600"
                      : "text-gray-500 hover:text-text-primary"
                  }`}
                >
                  Sessions
                </Link>
                {user?.role === "trainer" && (
                  <Link
                    to="/customers"
                    className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                      isActive("/customers")
                        ? "text-indigo-600 border-b-2 border-indigo-600"
                        : "text-gray-500 hover:text-text-primary"
                    }`}
                  >
                    Customers
                  </Link>
                )}
                {user?.role === "customer" && (
                  <Link
                    to="/trainers"
                    className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                      isActive("/trainers")
                        ? "text-indigo-600 border-b-2 border-indigo-600"
                        : "text-gray-500 hover:text-text-primary"
                    }`}
                  >
                    Trainers
                  </Link>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Profile Switcher Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary focus:outline-none"
                >
                  <div className="text-right hidden lg:block">
                    <div className="text-xs text-gray-500">Logged in as</div>
                    <div className="font-medium flex items-center gap-2">
                      {displayEmail}
                      {activeCustomer && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Editing
                        </span>
                      )}
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 transition-transform ${
                      profileDropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 max-h-96 overflow-y-auto bg-bg-primary rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {/* Current User */}
                      <div className="px-4 py-2 border-b border-border-light">
                        <div className="text-xs text-gray-500">
                          Your Account
                        </div>
                        <div className="font-medium text-text-primary">
                          {user?.email}
                        </div>
                        <div className="text-xs text-gray-500 capitalize">
                          {user?.role}
                        </div>
                      </div>

                      {/* Switch Back to Self (if currently acting as customer) */}
                      {activeProfileId && (
                        <button
                          onClick={() => {
                            switchProfile(null);
                            setProfileDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-gray-100 flex items-center gap-2 border-b border-border-light"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 19l-7-7m0 0l7-7m-7 7h18"
                            />
                          </svg>
                          Switch back to yourself
                        </button>
                      )}

                      {/* Switch to Customers (Trainers Only) */}
                      {user?.role === "trainer" &&
                        customersData?.customers &&
                        customersData.customers.length > 0 && (
                          <>
                            <div className="px-4 py-2 text-xs text-gray-500 font-semibold">
                              Switch to Customer
                            </div>
                            {customersData.customers.map((customer) => (
                              <button
                                key={customer.id}
                                onClick={() => {
                                  switchProfile(customer.id);
                                  setProfileDropdownOpen(false);
                                }}
                                disabled={activeProfileId === customer.id}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                                  activeProfileId === customer.id
                                    ? "bg-blue-50 text-blue-700"
                                    : "text-text-secondary"
                                }`}
                              >
                                <div>
                                  <div className="font-medium">
                                    {customer.name || customer.email}
                                  </div>
                                  {customer.name && (
                                    <div className="text-xs text-gray-500">
                                      {customer.email}
                                    </div>
                                  )}
                                </div>
                                {activeProfileId === customer.id ? (
                                  <svg
                                    className="w-4 h-4 text-blue-600"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-4 h-4 text-gray-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 5l7 7-7 7"
                                    />
                                  </svg>
                                )}
                              </button>
                            ))}
                          </>
                        )}

                      {/* Manage Customers Link (Trainers Only) - Always show */}
                      {user?.role === "trainer" && (
                        <Link
                          to="/customers"
                          onClick={() => setProfileDropdownOpen(false)}
                          className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 border-t border-border-light"
                        >
                          + Invite / Manage Customers
                        </Link>
                      )}

                      {/* Help Link */}
                      <Link
                        to="/help"
                        onClick={() => setProfileDropdownOpen(false)}
                        className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-gray-100 border-t border-border-light flex items-center gap-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Help
                      </Link>

                      {/* Settings Link */}
                      <Link
                        to="/settings"
                        onClick={() => setProfileDropdownOpen(false)}
                        className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-gray-100 border-t border-border-light flex items-center gap-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        Settings
                      </Link>

                      {/* Sign Out */}
                      <button
                        onClick={() => {
                          handleSignOut();
                          setProfileDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-border-light"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Connect Button */}
              <button
                onClick={() => {
                  navigate("/mirror/main");
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                <span className="w-2 h-2 bg-red-900 rounded-full"></span>
                Mirror
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Top Bar */}
      <nav className="bg-bg-primary shadow-sm md:hidden fixed top-0 left-0 right-0 z-10">
        <div className="px-4">
          <div className="flex justify-between items-center h-14">
            <Link to="/" className="flex items-center gap-2 text-lg font-bold">
              <img src="/icons/icon.png" alt="TrainrFlo" className="h-7 w-7" />
              <span>
                <span className="text-indigo-600">train</span>
                <span className="text-orange-500">r</span>
                <span className="text-indigo-600">flo</span>
              </span>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              className="p-2 rounded-md text-text-secondary hover:bg-gray-100"
            >
              {mobileMenuOpen ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="border-t border-border-light bg-bg-primary">
            <div className="px-4 py-3 space-y-1">
              {/* Current Profile */}
              <div className="pb-2 border-b border-border-light">
                <div className="text-xs text-gray-500">Your Account</div>
                <div className="text-sm font-medium text-text-primary">
                  {user?.email}
                </div>
                <div className="text-xs text-gray-500 capitalize">
                  {user?.role}
                </div>
              </div>

              {/* Currently Acting As */}
              {activeCustomer && (
                <div className="py-2 border-b border-border-light">
                  <div className="text-xs text-gray-500 mb-1">
                    Currently acting as
                  </div>
                  <div className="text-sm font-medium text-blue-700">
                    {activeCustomer.email}
                  </div>
                  <button
                    onClick={() => {
                      switchProfile(null);
                      setMobileMenuOpen(false);
                    }}
                    className="mt-2 w-full text-left px-3 py-1.5 text-sm text-text-secondary bg-gray-100 hover:bg-gray-200 rounded-md flex items-center gap-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                    Switch back to yourself
                  </button>
                </div>
              )}

              {/* Switch to Customers (Trainers Only) */}
              {user?.role === "trainer" &&
                customersData?.customers &&
                customersData.customers.length > 0 && (
                  <div className="py-2 border-b border-border-light">
                    <div className="text-xs text-gray-500 font-semibold mb-2">
                      Switch to Customer
                    </div>
                    {customersData.customers.map((customer) => (
                      <button
                        key={customer.id}
                        onClick={() => {
                          switchProfile(customer.id);
                          setMobileMenuOpen(false);
                        }}
                        disabled={activeProfileId === customer.id}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md mb-1 ${
                          activeProfileId === customer.id
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "text-text-secondary hover:bg-gray-100"
                        }`}
                      >
                        <div className="font-medium">
                          {customer.name || customer.email}
                        </div>
                        {customer.name && (
                          <div className="text-xs text-gray-500">
                            {customer.email}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

              {/* Manage Customers Link (Trainers Only) - Always show */}
              {user?.role === "trainer" && (
                <Link
                  to="/customers"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
                >
                  + Invite / Manage Customers
                </Link>
              )}

              {/* Help Link */}
              <Link
                to="/help"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full text-left px-3 py-2 text-sm text-text-secondary hover:bg-gray-100 rounded-md flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Help
              </Link>

              {/* Settings Link */}
              <Link
                to="/settings"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full text-left px-3 py-2 text-sm text-text-secondary hover:bg-gray-100 rounded-md flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Settings
              </Link>

              <button
                onClick={() => {
                  handleSignOut();
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md"
              >
                Sign Out
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Email verification banner */}
      {showEmailBanner && (
        <div className="bg-amber-50 border-b border-amber-200 fixed md:relative top-14 md:top-0 left-0 right-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-amber-800">
                Please verify your email address to unlock all features. We sent
                a link to <span className="font-medium">{user?.email}</span>.
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={handleResend}
                  disabled={resendStatus === "sending" || cooldown > 0}
                  className="px-3 py-1.5 text-xs font-medium rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendStatus === "sending"
                    ? "Sending…"
                    : cooldown > 0
                      ? `Resend (${cooldown}s)`
                      : "Resend email"}
                </button>
                {resendStatus === "sent" && cooldown === 0 && (
                  <span className="text-xs text-green-700">Sent!</span>
                )}
                {resendStatus === "error" && (
                  <span className="text-xs text-red-600">
                    Failed. Try again.
                  </span>
                )}
                <button
                  onClick={handleCheckVerified}
                  disabled={checkingVerification}
                  className="px-3 py-1.5 text-xs font-medium rounded-md bg-white text-amber-800 hover:bg-amber-50 border border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkingVerification ? "Checking…" : "I've verified"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTrainerBanner && (
        <div
          className={`bg-blue-600 text-white shadow-sm md:block fixed md:relative ${showEmailBanner ? "top-28" : "top-14"} md:top-0 left-0 right-0 z-40`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-sm font-medium">
                  Editing:{" "}
                  <span className="font-semibold">
                    {activeCustomer.name || activeCustomer.email}
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => switchProfile(null)}
                  className="px-3 py-1.5 text-sm bg-blue-700 hover:bg-blue-800 rounded-md font-medium whitespace-nowrap"
                >
                  Switch Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main
        className={`max-w-screen-lg mx-auto py-6 sm:px-6 lg:px-8 ${mainMt}`}
      >
        <Outlet />
      </main>

      <ChatSidebar />

      {/* Toast notification */}
      {toastMsg && (
        <div className="fixed bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 bg-gray-900 text-white text-sm rounded-lg shadow-lg whitespace-nowrap">
          {toastMsg}
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-bg-primary border-t border-border-light z-10">
        <div
          className={`grid ${
            user?.role === "trainer" ? "grid-cols-5" : "grid-cols-5"
          } h-16`}
        >
          <Link
            to="/programs"
            className={`flex flex-col items-center justify-center ${
              location.pathname.startsWith("/programs")
                ? "text-indigo-600"
                : "text-gray-500 hover:text-text-primary"
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <span className="text-xs mt-1">Programs</span>
          </Link>
          <Link
            to="/videos"
            className={`flex flex-col items-center justify-center ${
              isActive("/videos")
                ? "text-indigo-600"
                : "text-gray-500 hover:text-text-primary"
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-xs mt-1">Exercises</span>
          </Link>
          <Link
            to="/goals"
            className={`flex flex-col items-center justify-center ${
              location.pathname.startsWith("/programs")
                ? "text-indigo-600"
                : "text-gray-500 hover:text-text-primary"
            }`}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <circle cx={12} cy={12} r={9} strokeWidth={2} />
              <circle cx={12} cy={12} r={5} strokeWidth={2} />
              <circle
                cx={12}
                cy={12}
                r={1}
                fill="currentColor"
                strokeWidth={2}
              />
            </svg>
            <span className="text-xs mt-1">Targets</span>
          </Link>
          <Link
            to="/sessions"
            className={`flex flex-col items-center justify-center ${
              location.pathname.startsWith("/sessions")
                ? "text-indigo-600"
                : "text-gray-500 hover:text-text-primary"
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <span className="text-xs mt-1">Sessions</span>
          </Link>
          {user?.role === "trainer" && (
            <Link
              to="/customers"
              className={`flex flex-col items-center justify-center ${
                isActive("/customers")
                  ? "text-indigo-600"
                  : "text-gray-500 hover:text-text-primary"
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <span className="text-xs mt-1">Customers</span>
            </Link>
          )}
          {user?.role === "customer" && (
            <Link
              to="/trainers"
              className={`flex flex-col items-center justify-center ${
                isActive("/trainers")
                  ? "text-indigo-600"
                  : "text-gray-500 hover:text-text-primary"
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <span className="text-xs mt-1">Trainers</span>
            </Link>
          )}
        </div>
      </nav>
    </div>
  );
}
